// Hurricane / tropical storm system (WEATHER-SYSTEM.md §5) — the flagship.
//
// The lesson is the prep checklist, which mirrors real PR husbandry guidance.
// The practice drill is always available: nobody should need a real hurricane
// to learn this.
//
// Two doors lead in, and until the league one existed only the drill was wired.
// `beginStormWatch` is the drill's, and the live weather feed's: a player starts
// one by hand, and in Normal a real Open-Meteo warning starts one for them. The
// league forecast (REFOCUS §11) had no third door — it raised a storm alert on
// the pill, the pill showed a storm day a week out, and the day arrived and
// nothing happened. A forecast that promises and does not deliver is worse than
// no forecast, because it teaches a player that every alert in this game is
// decoration. `stepLeagueStorm` is that third door, and it is driven entirely by
// the season: no clock, no city, no save, no draw of its own.

import { bus } from '../core/events';
import { clamp, round2 } from '../core/math';
import type { ChickenEntity, GameState, StormAlert } from '../core/types';
import { addCondition } from './disease';
import { forecastWeek, type ForecastDay } from './forecast';
import { effectsOf } from './items';
import { leagueContext } from './league';
import { hourOfDay } from './time';

export interface StormChecklistItem {
  id: string;
  label: { en: string; es: string };
  /** Reads world state to decide whether the player has actually done it. */
  isComplete: (state: GameState) => boolean;
  /** What goes wrong if this one is skipped. */
  consequence: string;
}

export const STORM_CHECKLIST: StormChecklistItem[] = [
  {
    id: 'anchor_coop',
    label: { en: 'Anchor the coop', es: 'Ancla el gallinero' },
    isComplete: (state) =>
      state.structures.filter((s) => effectsOf(s).coop).every((s) => s.state.anchored === true),
    consequence: 'coop_damaged',
  },
  {
    id: 'stock_water',
    label: { en: 'Stock 3 days of water', es: 'Guarda agua para 3 días' },
    isComplete: (state) =>
      state.structures.some((s) => effectsOf(s).waterReserve && (s.state.water ?? 0) > 80) &&
      state.structures
        .filter((s) => effectsOf(s).providesWater)
        .every((s) => (s.state.water ?? 0) > 80),
    consequence: 'dehydration_risk',
  },
  {
    id: 'stock_feed',
    label: { en: 'Seal 3 days of feed', es: 'Sella comida para 3 días' },
    // Wet feed molds, which is why sealed storage is the real advice.
    isComplete: (state) => (state.inventory.feed_layer ?? 0) + (state.inventory.feed_grower ?? 0) + (state.inventory.feed_starter ?? 0) >= 3,
    consequence: 'moldy_feed',
  },
  {
    id: 'flock_inside',
    label: { en: 'Move the flock in early', es: 'Mete las gallinas temprano' },
    isComplete: (state) =>
      state.flock.every((c) => c.currentState === 'ROOSTING' || c.currentState === 'BROODY'),
    consequence: 'birds_exposed',
  },
  {
    id: 'clear_yard',
    label: { en: 'Clear loose objects', es: 'Recoge objetos sueltos' },
    isComplete: (state) => !state.structures.some((s) => effectsOf(s).decor),
    consequence: 'projectile_damage',
  },
  {
    id: 'charge_battery',
    label: { en: 'Charge the brooder battery', es: 'Carga la batería' },
    // Only relevant when there are chicks depending on a heat lamp.
    isComplete: (state) =>
      !state.flock.some((c) => c.stage === 'chick') || (state.inventory.lamp_battery ?? 0) > 0,
    consequence: 'chicks_cold',
  },
];

/** Items that apply to this farm right now — a chick-free farm skips the battery. */
export function applicableChecklist(state: GameState): StormChecklistItem[] {
  return STORM_CHECKLIST.filter(
    (item) => item.id !== 'charge_battery' || state.flock.some((c) => c.stage === 'chick'),
  );
}

export function evaluateChecklist(state: GameState): {
  completed: string[];
  missed: string[];
  score: number;
} {
  const applicable = applicableChecklist(state);
  const completed = applicable.filter((item) => item.isComplete(state)).map((i) => i.id);
  const missed = applicable.filter((item) => !item.isComplete(state)).map((i) => i.id);
  return {
    completed,
    missed,
    score: Math.round((completed.length / applicable.length) * 100),
  };
}

/** Hours a league storm's watch sits on the pill before the storm day. */
export const LEAGUE_STORM_ETA_HOURS = 12;

export interface LeagueStormSchedule {
  /** The league day the storm itself lands on. */
  stormDay: number;
  /** The league day the watch raises — the first day the player is told. */
  watchDay: number;
  /** The hour of `stormDay` the storm lands. */
  hitHour: number;
}

/**
 * How many league days a storm is warned for, chosen rather than defaulted.
 *
 * REFOCUS §11c asks for warnings at real distress lines and layers the notice
 * so signs come first and the warning is the loudest, last thing. Two days is
 * the smallest window that buys a *whole calendar day* of daylight, which is
 * what the checklist costs in real terms: the storm supplies in the shop are
 * gated behind `storm_watch_or_drill`, so the watch is the thing that opens the
 * shop, and buying a tie-down kit, three feed and a water barrel is an errand,
 * not a tap. One league day is "tonight" and makes the outcome a function of
 * what hour the player happened to be playing. Three or more is worse in the
 * other direction: the pill already shows storm flags across the whole forecast
 * week, so a longer watch says nothing new, and it re-opens the storm shop for
 * days on which there is nothing yet to do about.
 *
 * It is a league day and not a wall-clock duration for the reason D7 gives: a
 * league day is only comparable if every player gets the warning at the same
 * point in it. Anchoring to the day index means a player who plays at 9am and a
 * player who plays at midnight are both told on the same league day, and both
 * have the same daylight to act — which is the property that makes the resulting
 * scores comparable at all.
 */
export const LEAGUE_STORM_WATCH_DAYS = 2;

/** A storm day's schedule, or null for a day the forecast says is clear. */
function scheduleFor(day: ForecastDay): LeagueStormSchedule | null {
  // The forecast's own alert, not a second opinion computed here: a day the pill
  // has already marked as a storm day is exactly the day that gets a storm.
  if (day.stormAlert === 'none') return null;
  return {
    stormDay: day.leagueDay,
    watchDay: day.leagueDay - LEAGUE_STORM_WATCH_DAYS,
    hitHour: LEAGUE_STORM_ETA_HOURS,
  };
}

/** One league day's schedule, or null for a day with no storm in it. */
export function leagueStormDay(seasonId: string, leagueDay: number): LeagueStormSchedule | null {
  return scheduleFor(forecastWeek(seasonId, leagueDay).days[0]);
}

/**
 * The next league storm at or after `fromDay`, searched over the whole forecast
 * week, or null when the week ahead is clear.
 *
 * Read straight off the one week the forecast already derived rather than by
 * asking for a day at a time: `forecastWeek` memoises a single slot precisely so
 * a per-tick caller costs one derivation, and a loop of per-day asks would miss
 * that slot every time and re-roll the week once a second for the whole season.
 *
 * Searching from the current day rather than from the watch day is what keeps a
 * storm a fact about the league rather than about whoever was looking. A save
 * that has passed its storm day does not get that storm late; it waits for the
 * next one, because a hurricane does not pause for a player who was away.
 */
export function nextLeagueStorm(seasonId: string, fromDay: number): LeagueStormSchedule | null {
  for (const day of forecastWeek(seasonId, fromDay).days) {
    const schedule = scheduleFor(day);
    if (schedule) return schedule;
  }
  return null;
}

/** Raises a watch. The drill runs the same code path as the real thing. */
export function beginStormWatch(state: GameState, isDrill: boolean, etaHours = 10): void {
  state.storm = {
    active: true,
    isDrill,
    completed: [],
    startedAtHour: state.simHour,
    hitsAtHour: state.simHour + etaHours,
  };
  state.weather.stormAlert = isDrill ? 'none' : 'watch';
  state.weather.stormEtaHour = state.simHour + etaHours;
  bus.emit('STORM_WATCH', {
    alert: isDrill ? 'watch' : state.weather.stormAlert,
    weather: state.weather,
  });
}

export interface StormOutcome {
  score: number;
  missed: string[];
  consequences: string[];
  eggitsAwarded: number;
  badgeEarned: boolean;
}

/**
 * A bird caught in the open by the storm: frightened and soaked.
 *
 * Shared by the two checklist gaps that end in the same place — a flock left out
 * of the coop, and a coop that came apart with birds still roosting in it.
 */
function caughtInTheStorm(chicken: ChickenEntity, simHour: number, severity: 'mild' | 'moderate'): void {
  addCondition(chicken, 'scared', simHour, severity);
  if (severity === 'moderate') addCondition(chicken, 'wet_and_cold', simHour, severity);
}

/**
 * The storm arrives. Prep decides the outcome — full prep earns the badge,
 * gaps cause sick birds and damage. Never death (EDUCATION-DESIGN.md §1.2).
 */
export function resolveStorm(state: GameState): StormOutcome {
  const { completed, missed, score } = evaluateChecklist(state);
  const consequences: string[] = [];
  let coopCameApart = false;

  for (const id of missed) {
    const item = STORM_CHECKLIST.find((i) => i.id === id);
    if (!item) continue;
    consequences.push(item.consequence);

    switch (item.consequence) {
      case 'coop_damaged':
        for (const structure of state.structures) {
          if (!effectsOf(structure).coop) continue;
          structure.state.damaged = true;
          coopCameApart = true;
        }
        break;
      case 'birds_exposed':
        for (const chicken of state.flock) {
          if (chicken.currentState !== 'ROOSTING') caughtInTheStorm(chicken, state.simHour, 'moderate');
        }
        break;
      case 'dehydration_risk':
        for (const structure of state.structures) {
          if (effectsOf(structure).providesWater) structure.state.water = 0;
        }
        break;
      case 'moldy_feed':
        for (const structure of state.structures) {
          if (effectsOf(structure).providesFeed) structure.state.feed = 0;
        }
        break;
      case 'projectile_damage':
      case 'chicks_cold':
        for (const chicken of state.flock) {
          if (chicken.stage === 'chick') caughtInTheStorm(chicken, state.simHour, 'mild');
        }
        break;
    }
  }

  // A coop that is not anchored comes off its base, and a bird roosting in it
  // goes with it. Nothing read `damaged` before this, so skipping the anchor
  // cost a flag and no hen anything — the action was busywork. What actually
  // goes wrong to a hen is being in a structure that is no longer one.
  if (coopCameApart) {
    for (const chicken of state.flock) {
      if (chicken.currentState === 'ROOSTING') caughtInTheStorm(chicken, state.simHour, 'moderate');
    }
  }

  // Every storm leaves debris — cleanup teaches recovery, not just prep.
  const rngLikeSpread = Math.max(2, Math.floor(state.grid.tiles.length * 0.15));
  for (let i = 0; i < rngLikeSpread; i++) {
    const tile = state.grid.tiles[(i * 7) % state.grid.tiles.length];
    tile.debris = true;
  }

  const perfect = missed.length === 0;
  // A drill is practice, and practice pays practice money. Without the day
  // guard, raising a drill and resolving it in a loop paid out every time with
  // no cap and no prep required — 20 calls turned 50 Eggits into 210.
  const full = perfect ? 50 : Math.round(score / 4);
  const day = Math.floor(state.simHour / 24);
  const alreadyPaidToday = state.progression.lastStormPayoutDay === day;
  const eggitsAwarded = alreadyPaidToday ? 0 : state.storm.isDrill ? Math.round(full / 4) : full;
  if (eggitsAwarded > 0) {
    state.eggits += eggitsAwarded;
    state.progression.lastStormPayoutDay = day;
  }

  let badgeEarned = false;
  if (perfect && !state.progression.badges.includes('storm_ready')) {
    state.progression.badges.push('storm_ready');
    badgeEarned = true;
    bus.emit('BADGE_EARNED', { badge: 'storm_ready' });
  }

  state.storm = {
    active: true,
    isDrill: state.storm.isDrill,
    completed,
    startedAtHour: state.storm.startedAtHour,
    score,
    // Carried rather than dropped. The league path stamps the day before it
    // resolves, and the storm event is emitted from inside this function, so
    // rebuilding the object without it would erase the only record of which
    // league day the hurricane belonged to. A drill never sets it, and carries
    // whatever the last cleared league storm left — which is nothing.
    resolvedOnLeagueDay: state.storm.resolvedOnLeagueDay,
  };
  state.weather.stormAlert = 'cleanup';
  bus.emit('STORM_PASSED', { score, isDrill: state.storm.isDrill });

  return { score, missed, consequences, eggitsAwarded, badgeEarned };
}

/** Post-storm cleanup: clear debris tiles, check water purity. */
export function cleanupStorm(state: GameState): { tilesCleared: number } {
  let tilesCleared = 0;
  for (const tile of state.grid.tiles) {
    if (tile.debris) {
      tile.debris = false;
      tilesCleared++;
    }
  }
  for (const structure of state.structures) {
    if (effectsOf(structure).providesWater) {
      // Storm water is contaminated water — a real post-storm task.
      structure.state.waterPurity = clamp((structure.state.waterPurity ?? 100) - 40, 0, 100);
    }
  }
  state.storm = { active: false, isDrill: false, completed: [] };
  state.weather.stormAlert = 'none';
  return { tilesCleared };
}

/** Coops in the yard that a hurricane has not been tied down against. */
export function coopsToAnchor(state: GameState): number {
  return state.structures.filter((s) => effectsOf(s).coop && !s.state.anchored).length;
}

/**
 * Whether anchoring is on the list this yard can act on right now: a watch or a
 * drill is up, there is a coop, and it is still loose.
 *
 * The "still loose" half matters as much as the rest. `applicableChores` is
 * what the daily wage is scored against, and a chore that stayed on the list
 * after it was done would make that wage unreachable for the rest of the
 * watch.
 */
export function stormAnchorDue(state: GameState): boolean {
  return (state.storm.active || state.weather.stormAlert !== 'none') && coopsToAnchor(state) > 0;
}

/**
 * Drives every coop down, and returns how many needed it.
 *
 * A tie-down kit in the pack is spent when there is one, because that is what
 * the kit is for. Its absence does NOT refuse the work, and that is the whole
 * fix for the dead path: the game has no store screen, so a kit is unobtainable
 * in play, and a rule that made the purchase a precondition meant "Anchor the
 * coop" could never be completed and the storm checklist could never reach 100.
 * Stakes, straps and a hammer are what the real advice asks for, and a keeper
 * who owns no kit still ties the coop down.
 */
export function anchorCoops(state: GameState): number {
  let anchored = 0;
  for (const structure of state.structures) {
    if (effectsOf(structure).coop && !structure.state.anchored) {
      structure.state.anchored = true;
      anchored++;
    }
  }
  if (anchored > 0 && (state.inventory.tie_down_kit ?? 0) > 0) state.inventory.tie_down_kit -= 1;
  return anchored;
}

/** Hours until the storm arrives, for the HUD countdown. */
export function hoursUntilStorm(state: GameState): number | null {
  if (!state.storm.active || state.storm.hitsAtHour === undefined) return null;
  return round2(Math.max(0, state.storm.hitsAtHour - state.simHour));
}

export function shouldStormHit(state: GameState): boolean {
  return (
    state.storm.active &&
    state.storm.hitsAtHour !== undefined &&
    state.simHour >= state.storm.hitsAtHour &&
    state.weather.stormAlert !== 'cleanup'
  );
}

/**
 * Runs the league storm, and does nothing at all outside a league.
 *
 * Called from `stepSimulation` rather than from the game loop, and that is the
 * whole of the determinism argument. `replayActionLog` drives the sim by calling
 * `stepSimulation` and nothing else, so a storm resolved in the app's frame loop
 * would be a storm the server's replay never sees — a Veteran result that
 * depends on whether the phone or the server was paying attention. The drill
 * still resolves in the game loop exactly as before, because a drill is a
 * player action and lives in the action log's world; the league storm is not a
 * player action at all, it is a property of the season, and so it belongs
 * beside the other season-derived facts.
 */
export function stepLeagueStorm(state: GameState): void {
  const league = leagueContext(state);
  if (!league) return;

  // A drill the player raised themselves. It resolves through the game loop and
  // a league storm would collide with it, so the drill keeps the yard.
  if (state.storm.active) {
    if (
      state.weather.stormAlert === 'cleanup' &&
      state.storm.resolvedOnLeagueDay !== undefined &&
      state.storm.resolvedOnLeagueDay < league.leagueDay
    ) {
      // Clearing on the league-day turnover, not on a wall clock, is what keeps
      // the window the same for every player. `cleanupStorm` also purges the
      // alert, so this is what un-gates the storm supplies for the next one.
      cleanupStorm(state);
    }
    if (shouldStormHit(state)) {
      // Stamped before the resolve rather than after: `resolveStorm` emits
      // STORM_PASSED, and a listener on that event is the first place anyone
      // can ask which league day this hurricane belonged to. `resolveStorm`
      // rewrites the rest of the storm and leaves this field alone.
      state.storm.resolvedOnLeagueDay = league.leagueDay;
      resolveStorm(state);
    }
    return;
  }

  const schedule = nextLeagueStorm(league.seasonId, league.leagueDay);
  if (!schedule || league.leagueDay < schedule.watchDay) return;
  // A day already under way when the watch first raises — a save that crossed
  // into the window while closed, or a reload partway through — is hit partway
  // through rather than skipped, because the storm is not optional.
  const hoursIn = hourOfDay(state.simHour);
  const eta = Math.max(0, schedule.hitHour - hoursIn + 24 * (schedule.stormDay - league.leagueDay));
  // `beginStormWatch` replaces the storm wholesale, so a league storm arrives
  // with `isDrill` false and no resolved day — which is what the cleanup above
  // and the payout guard in `resolveStorm` both read.
  beginStormWatch(state, false, eta);
}

export function alertLabel(alert: StormAlert, locale: 'en' | 'es'): string {
  const labels: Record<StormAlert, { en: string; es: string }> = {
    none: { en: '', es: '' },
    watch: { en: 'Storm Watch', es: 'Vigilancia de tormenta' },
    warning: { en: 'Hurricane Warning', es: 'Aviso de huracán' },
    hit: { en: 'Storm hitting', es: 'Tormenta azotando' },
    cleanup: { en: 'Cleanup', es: 'Limpieza' },
  };
  return labels[alert][locale];
}
